import { isAxiosError } from 'axios';
import type { Package, PackageCatalogEntry } from '@/hooks/use-software-update-packages';
import type { PackageInUseError } from '@/lib/software-update-package.service';

export const MACHINE_TYPES = ['rower', 'bike', 'treadmill'] as const;
export const ATS_IDS = ['ats1', 'ats2', 'ats5', 'ats6', 'atsa'] as const;

/** A package row with its resolved definition attached for display. */
export type RowPackage = Package & {
  catalogEntry?: PackageCatalogEntry;
};

export function packageKey(type: string, packageName: string) {
  return `${type}:${packageName}`;
}

/** Catalog key for anything carrying a package `type` + `package_name` (package, slot, rollout package). */
export function packageKeyOf(item: { type: string; package_name: string }) {
  return packageKey(item.type, item.package_name);
}

/**
 * Resolves the human-facing label for a package, preferring the catalog entry,
 * then the backend-provided display name, then the raw package name. Single source
 * of truth so the table, detail, edit, and delete views never drift.
 */
export function packageDisplayLabel(pkg: {
  display_name?: string;
  package_name?: string;
  catalogEntry?: PackageCatalogEntry;
}) {
  return pkg.catalogEntry?.display_name || pkg.display_name || pkg.package_name || '-';
}

/** Border-only panel used inside dialogs to frame read-only package details. */
export const detailBoxSx = {
  borderRadius: 2,
  border: '1px solid',
  borderColor: 'divider',
  p: 1.5,
  display: 'grid',
  gap: 1,
} as const;

export function packageFileLabel(pkg: Package) {
  return pkg.file_name || '-';
}

export function formatToken(value: string) {
  return value ? value[0].toUpperCase() + value.slice(1) : value;
}

/** Parses the 409 body returned when a package is still referenced by rollouts. */
export function packageInUseError(err: unknown): PackageInUseError | null {
  if (!isAxiosError(err) || err.response?.status !== 409) return null;
  const data = err.response.data as Partial<PackageInUseError> | undefined;
  if (!data || !Array.isArray(data.rollouts)) return null;
  return {
    message: data.message || 'Package is used by rollouts',
    rollouts: data.rollouts,
  };
}

export function extractPackageError(err: unknown, fallback: string) {
  if (!isAxiosError(err)) return fallback;
  const data = err.response?.data as { detail?: string; message?: string; error?: { message?: string } } | undefined;
  return data?.error?.message || data?.detail || data?.message || fallback;
}
